import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Tabs, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from 'atoms';
import { useQueryParams } from 'hooks';
import { getClientList } from 'store/clientSlice';
import {
	PaymentQueueTab,
	PaymentHistoryTab,
	FinancePaymentFilters,
	ProFormaInvoiceModal,
	PaymentDetailsModal,
	UploadProformaModal,
} from './components';
import {
	fetchPaymentQueue,
	fetchPaymentHistory,
	generateProformaInvoice,
	approvePayment,
	rejectPayment,
} from 'store/financeSlice';
import {
	fetchPaymentQueueV2,
	fetchPaymentHistoryV2,
	generateProformaInvoiceV2,
	approvePaymentV2,
	rejectPaymentV2,
	searchEmployeeV2,
} from 'store/financeSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import { getUIVersion } from 'utils/router-static-data';

const { TabPane } = Tabs;

const PaymentQueue = 'payment-queue';
const PaymentHistory = 'payment-history';

const getFilenameFromUrl = (url) => {
	if (!url) return null;
	return url.startsWith('s3://') ? url.replace('s3://logitrack/', '') : url;
};

const constructFilenameFromInvoiceNumber = (invoiceNumber) => {
	if (!invoiceNumber) return null;
	return invoiceNumber.includes('.') ? invoiceNumber : `${invoiceNumber}.pdf`;
};

function PaymentsPage() {
	const dispatch = useDispatch();
	const isV2 = useMemo(() => getUIVersion() === 'new', []);
	const currentUsername = useSelector(
		(state) => state?.auth?.me?.username || state?.auth?.me?.user_name
	);
	const [activeTab, setActiveTab] = useState(PaymentQueue);
	const [employeeId, setEmployeeId] = useState(null);

	// State for each tab
	const [paymentQueueData, setPaymentQueueData] = useState({ rows: [], count: 0 });
	const [paymentHistoryData, setPaymentHistoryData] = useState({ rows: [], count: 0 });
	const [uploadProformaModalVisible, setUploadProformaModalVisible] = useState(false);

	// Modal states
	const [proformaModalVisible, setProformaModalVisible] = useState(false);
	const [paymentDetailsModalVisible, setPaymentDetailsModalVisible] = useState(false);
	const [selectedRecord, setSelectedRecord] = useState(null);
	const [loading, setLoading] = useState({
		generateProforma: false,
		approvePayment: false,
		rejectPayment: false,
	});

	// Fetch employee ID by username
	useEffect(() => {
		if (currentUsername) {
			const action = isV2
				? searchEmployeeV2({ query_str: currentUsername, limit: 1, offset: 0 })
				: getClientList({
						search_type: 'employee',
						offset: 0,
						limit: 1,
						filter_by: {
							query_str: currentUsername,
						},
				  });
			dispatch(action)
				.then((response) => {
					const employee = response?.data?.[0];
					if (employee?.id) {
						setEmployeeId(employee.id);
					}
				})
				.catch((error) => {
					console.error('Failed to fetch employee ID:', error);
				});
		}
	}, [currentUsername, dispatch, isV2]);

	// Pagination config for each tab using useQueryParams
	const [paymentQueuePagination, setPaymentQueuePagination] = useQueryParams({
		search_type: 'finance_payment',
		fetch_filters: false,
		business_unit_ids: [],
		persistKey: 'financePaymentQueue:filters',
	});

	const [paymentHistoryPagination, setPaymentHistoryPagination] = useQueryParams({
		search_type: 'finance_payment_history',
		fetch_filters: false,
		business_unit_ids: [],
		persistKey: 'financePaymentHistory:filters',
	});

	// Helper function to handle business unit IDs for each tab
	const getBusinessUnitIds = useCallback((paginationConfig) => {
		const buIds = paginationConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	}, []);

	// Dispatch actions for each tab
	const memoizedPaymentQueueAction = useCallback(
		(params) => {
			const currentConfig = paymentQueuePagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				query_str: currentConfig?.query_str,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
			};
			const action = isV2 ? fetchPaymentQueueV2 : fetchPaymentQueue;
			return async (dispatchThunk) =>
				dispatchThunk(action(filterParams)).then((result) => ({
					rows: result.rows || [],
					count: result.count || 0,
				}));
		},
		[paymentQueuePagination, getBusinessUnitIds, isV2]
	);

	const memoizedPaymentHistoryAction = useCallback(
		(params) => {
			const currentConfig = paymentHistoryPagination;
			const filterParams = {
				limit: params.limit || 10,
				offset: params.offset || 0,
				query_str: currentConfig?.query_str,
				city: currentConfig?.city,
				client_id: currentConfig?.client_id,
				project_id: currentConfig?.project_id,
				vendor_id: currentConfig?.vendor_id,
				business_unit_ids: getBusinessUnitIds(currentConfig),
				from_date: currentConfig?.from_date,
				to_date: currentConfig?.to_date,
			};
			const action = isV2 ? fetchPaymentHistoryV2 : fetchPaymentHistory;
			return async (dispatchThunk) =>
				dispatchThunk(action(filterParams)).then((result) => ({
					rows: result.rows || [],
					count: result.count || 0,
				}));
		},
		[paymentHistoryPagination, getBusinessUnitIds, isV2]
	);

	// Handler for viewing pro-forma invoice
	const handleViewProformaInvoice = useCallback((record) => {
		setSelectedRecord(record);
		// GST and non-GST vendors can both generate or upload a pro-forma when none exists yet.
		if (!record?.vendor_proforma_invoice_number) {
			setUploadProformaModalVisible(true);
		} else {
			setProformaModalVisible(true);
		}
	}, []);

	// Handler for generating pro-forma invoice (called from modal)
	const handleGenerateInvoice = useCallback(
		(record) => {
			const action = isV2 ? generateProformaInvoiceV2 : generateProformaInvoice;
			return dispatch(action({ finance_request_id: record.id }))
				.then((result) => {
					if (result) {
						message.success('Pro-forma invoice generated successfully');
						// Refresh the table data
						setPaymentQueueData((prev) => ({
							...prev,
							rows: prev.rows.map((row) =>
								row.id === record.id
									? {
											...row,
											vendor_proforma_invoice_number:
												result.proforma_invoice_number ||
												result.invoice_number ||
												row.vendor_proforma_invoice_number,
											vendor_proforma_invoice_url:
												result.pdf_url ||
												result.document_url ||
												row.vendor_proforma_invoice_url,
									  }
									: row
							),
						}));
						return result;
					}
					message.error('Failed to generate pro-forma invoice');
					return null;
				})
				.catch((error) => {
					message.error(error.message || 'Failed to generate pro-forma invoice');
					throw error;
				});
		},
		[dispatch, isV2]
	);

	// Handler for approving payment
	const handleApprovePayment = useCallback((record) => {
		if (!record.vendor_proforma_invoice_number) {
			message.error('Please generate pro-forma invoice first');
			return;
		}
		setSelectedRecord(record);
		setPaymentDetailsModalVisible(true);
	}, []);

	// Handler for submitting payment details (approval)
	const handleSubmitPaymentDetails = useCallback(
		(paymentData) => {
			if (!employeeId) {
				message.error('Employee ID not found. Please refresh the page.');
				return;
			}

			setLoading((prev) => ({ ...prev, approvePayment: true }));
			const action = isV2
				? approvePaymentV2({ ...paymentData, login_user_id: employeeId })
				: approvePayment({ ...paymentData, login_user_id: employeeId });
			dispatch(action)
				.then(() => {
					message.success('Payment approved successfully');
					// Refresh the payment queue table
					memoizedPaymentQueueAction({ limit: 10, offset: 0 })(dispatch).then(
						(result) => {
							setPaymentQueueData(result);
						}
					);
					setPaymentDetailsModalVisible(false);
					setSelectedRecord(null);
				})
				.catch((error) => {
					message.error(error.message || 'Failed to approve payment');
				})
				.finally(() => {
					setLoading((prev) => ({ ...prev, approvePayment: false }));
				});
		},
		[dispatch, employeeId, memoizedPaymentQueueAction, isV2]
	);

	// Handler for rejecting payment
	const handleRejectPayment = useCallback(
		(record) => {
			setLoading((prev) => ({ ...prev, rejectPayment: true }));
			const action = isV2
				? rejectPaymentV2({ finance_request_id: record.id })
				: rejectPayment({ finance_request_id: record.id });
			dispatch(action)
				.then(() => {
					message.success('Payment rejected successfully');
					// Refresh the payment queue table
					memoizedPaymentQueueAction({ limit: 10, offset: 0 })(dispatch).then(
						(result) => {
							setPaymentQueueData(result);
						}
					);
				})
				.catch((error) => {
					message.error(error.message || 'Failed to reject payment');
				})
				.finally(() => {
					setLoading((prev) => ({ ...prev, rejectPayment: false }));
				});
		},
		[dispatch, memoizedPaymentQueueAction, isV2]
	);

	// Handler for downloading pro-forma invoice
	const handleDownloadProforma = useCallback(
		(record) => {
			let filename;
			if (isV2) {
				// V2
				filename =
					record?.vendor_proforma_invoice_key ||
					getFilenameFromUrl(record?.vendor_proforma_invoice_url) ||
					constructFilenameFromInvoiceNumber(record?.vendor_proforma_invoice_number);
			} else {
				// V1: original logic
				filename =
					getFilenameFromUrl(record?.vendor_proforma_invoice_url) ||
					constructFilenameFromInvoiceNumber(record?.vendor_proforma_invoice_number);

				if (!filename) {
					message.error('No invoice file found');
					return;
				}
			}

			if (!filename) {
				message.error('No invoice file found');
				return;
			}

			const data = {
				url_type: 'download',
				data: {
					entity: 'trip',
					keys: [filename],
				},
			};

			const signedUrlAction = getSignedUrl;
			dispatch(signedUrlAction(data))
				.then((response) => {
					if (response?.data && response.data[0]?.url) {
						window.open(response.data[0].url, '_blank');
					} else {
						message.error('Failed to get download URL');
					}
				})
				.catch((error) => {
					message.error(error.message || 'Failed to download pro-forma invoice');
				});
		},
		[dispatch, isV2]
	);

	// Handler for successful proforma upload
	const handleUploadProformaSuccess = useCallback(() => {
		// Refresh the payment queue table
		memoizedPaymentQueueAction({ limit: 10, offset: 0 })(dispatch).then((data) => {
			setPaymentQueueData(data);
		});
		setUploadProformaModalVisible(false);
		setSelectedRecord(null);
	}, [dispatch, memoizedPaymentQueueAction]);

	return (
		<Box mt={4}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Payment Queue" key={PaymentQueue}>
					<FinancePaymentFilters
						paginationConfig={paymentQueuePagination}
						setPaginationConfig={setPaymentQueuePagination}
						filterKey="PAYMENT_QUEUE"
					/>
					<PaymentQueueTab
						tableDatas={[paymentQueueData, setPaymentQueueData]}
						togglePaginate={[paymentQueuePagination, setPaymentQueuePagination]}
						dispatchAction={memoizedPaymentQueueAction}
						handleApprovePayment={handleApprovePayment}
						handleRejectPayment={handleRejectPayment}
						handleViewInvoice={handleViewProformaInvoice}
					/>
				</TabPane>
				<TabPane tab="History" key={PaymentHistory}>
					<FinancePaymentFilters
						paginationConfig={paymentHistoryPagination}
						setPaginationConfig={setPaymentHistoryPagination}
						filterKey="PAYMENT_HISTORY"
					/>
					<PaymentHistoryTab
						tableDatas={[paymentHistoryData, setPaymentHistoryData]}
						togglePaginate={[paymentHistoryPagination, setPaymentHistoryPagination]}
						dispatchAction={memoizedPaymentHistoryAction}
					/>
				</TabPane>
			</Tabs>

			{/* Pro-Forma Invoice Modal */}
			<ProFormaInvoiceModal
				visible={proformaModalVisible}
				onClose={() => {
					setProformaModalVisible(false);
					setSelectedRecord(null);
				}}
				record={selectedRecord}
				onGenerate={handleGenerateInvoice}
				onDownload={handleDownloadProforma}
				onReuploadSuccess={() => {
					memoizedPaymentQueueAction({ limit: 10, offset: 0 })(dispatch).then((data) => {
						setPaymentQueueData(data);
					});
				}}
			/>

			{/* Payment Details Modal */}
			<PaymentDetailsModal
				visible={paymentDetailsModalVisible}
				onClose={() => {
					setPaymentDetailsModalVisible(false);
					setSelectedRecord(null);
				}}
				record={selectedRecord}
				onSubmit={handleSubmitPaymentDetails}
				loading={loading.approvePayment}
			/>

			{/* Upload Pro-Forma Modal */}
			<UploadProformaModal
				visible={uploadProformaModalVisible}
				onClose={() => {
					setUploadProformaModalVisible(false);
					setSelectedRecord(null);
				}}
				record={selectedRecord}
				onUploadSuccess={handleUploadProformaSuccess}
				onGenerate={handleGenerateInvoice}
			/>
		</Box>
	);
}

export default PaymentsPage;
